import { useState, FormEvent, useImperativeHandle, forwardRef, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui/Spinner';
import { useCreateLedgerValue } from '@/api/hooks/useLedgerValues';
import { useAuth } from '@/contexts/AuthContext';
import { useDefaultDate } from '@/hooks/useDefaultDate';
import { ExpandableForm } from '@/components/ExpandableForm';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { IconButton } from '@/components/ui/IconButton';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useCurrencyFractionDigits } from '@/hooks/useCurrencyFractionDigits';
import { decimalToMinorUnits } from '@/utils/money';

export interface AddValueFormHandle {
    /** Collapse the form programmatically. */
    collapse: () => void;
}

interface AddValueFormProps {
    /** The ledger ID to add value to. */
    ledgerId: number;
    /** The user's max value date across all ledgers (for smart defaults). */
    userMaxValueDate?: string;
    /** The currency used in the latest value (for smart defaults). */
    defaultCurrency?: string;
    /** Callback when form is opened (e.g., to close edit rows). */
    onFormOpen?: () => void;
    /** Callback when value is successfully created. */
    onSuccess?: () => void;
    /** Label for the entity type (e.g., "value" or "balance"). */
    entityLabel?: string;
}

/**
 * Expandable form for adding a new value to a ledger.
 * Collapsed by default with "Add new value" label.
 * Expanded form contains: date picker, amount input, currency select.
 * Manages its own expanded state to prevent collapse on errors.
 */
export const AddValueForm = forwardRef<AddValueFormHandle, AddValueFormProps>(function AddValueForm({
    ledgerId,
    userMaxValueDate,
    defaultCurrency,
    onFormOpen,
    onSuccess,
    entityLabel = 'value',
}, ref) {
    // Capitalize first letter for display
    const EntityLabel = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
    const { settings } = useAuth();
    const defaultDate = useDefaultDate(userMaxValueDate);
    const createValue = useCreateLedgerValue(ledgerId);

    // Internal expanded state - form manages its own visibility
    const [isExpanded, setIsExpanded] = useState(false);

    // Expose collapse method to parent via ref
    useImperativeHandle(ref, () => ({
        collapse: () => setIsExpanded(false),
    }));

    const handleToggle = () => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);
        if (willExpand) {
            onFormOpen?.();
        }
    };

    // Compute the default currency: ledger's last currency, then user's display currency, then USD
    const computeDefaultCurrency = defaultCurrency || settings?.displayCurrency || 'USD';

    // Form state
    const [date, setDate] = useState(defaultDate);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState(computeDefaultCurrency);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const fractionDigits = useCurrencyFractionDigits(currency);

    // Sync date when default changes (handles async data loading)
    useEffect(() => {
        setDate(defaultDate);
    }, [defaultDate]);

    const resetForm = () => {
        setDate(defaultDate);
        setAmount('');
        setCurrency(computeDefaultCurrency);
        setErrors({});
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        setErrors({});

        // Validation
        const newErrors: Record<string, string> = {};

        if (!date) {
            newErrors.date = 'Date is required';
        }

        if (!amount.trim()) {
            newErrors.amount = 'Amount is required';
        }

        if (!currency) {
            newErrors.currency = 'Currency is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let valueInMinor: number;
        try {
            valueInMinor = decimalToMinorUnits(amount, fractionDigits);
        } catch (error) {
            setErrors({ amount: error instanceof Error ? error.message : 'Invalid amount' });
            return;
        }

        try {
            await createValue.mutateAsync({
                date, // Send Y-m-d date string directly
                value: valueInMinor,
                currency,
            });

            toast.success(`${EntityLabel} added successfully`);
            resetForm();
            setIsExpanded(false);
            onSuccess?.();
        } catch (error) {
            const isTransient = error instanceof ApiError && error.isTransient;
            toast.error(`Failed to add ${entityLabel}.${isTransient ? ' Please try again.' : ''}`);
            console.error(`Failed to create ${entityLabel}:`, error);
        }
    };

    const headerActions = (
        <IconButton
            variant="accent"
            size="md"
            onClick={(e) => {
                e.stopPropagation();
                handleSubmit();
            }}
            disabled={createValue.isPending}
            aria-label={`Add ${entityLabel}`}
            title={`Add ${EntityLabel}`}
        >
            {createValue.isPending ? (
                <Spinner />
            ) : (
                <CheckIcon className="h-5 w-5" />
            )}
        </IconButton>
    );

    return (
        <ExpandableForm
            title={`Add new ${entityLabel}`}
            isExpanded={isExpanded}
            onToggle={handleToggle}
            headerActions={headerActions}
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        error={errors.date}
                        required
                    />
                    <MoneyInput
                        label="Amount"
                        value={amount}
                        currency={currency}
                        onValueChange={setAmount}
                        onCurrencyChange={setCurrency}
                        error={errors.amount || errors.currency}
                        placeholder="0.00"
                    />
                </div>
                {/* Hidden submit button to enable Enter key submission */}
                <button type="submit" className="hidden" aria-hidden="true" />
            </form>
        </ExpandableForm>
    );
});
